import { useCallback, useEffect, useState } from 'react';
import type { Reserva } from '../../../api/ops/reservas';
import type { VistaPreviaSalida } from '../../../api/ops/reservasGestion';
import { claveIdempotencia } from '../../../api/comun';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Ventana } from '../../../componentes/Ventana';
import { dinero } from '../../../logica/dinero';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { nombreDe, todas, useNombres, type ContextoReservas } from '../contexto';
import { diaAbreviado, diaCorto, diaLargo, horaEn, hoyEn } from '../fechas';
import { mayuscula } from '../textos';

/**
 * 8.9 Estancias, la parte de Recepción (entrada propia del menú `estancias`, G878; G406, sin plano):
 * Llegadas de hoy · Dentro · Salidas de hoy (`listReservations?kind=stay`, G880: la jornada de hoy y las `arrived`). Check-in: si no tiene alojamiento, se elige uno de su zona
 * (`assignReservationResources`) y la llegada sale con `validatePass` (`manual`) sobre su pase, con «Deshacer» 5 s (G159).
 * Check-out (`checkOutStay`, con nota); la salida anticipada enseña antes lo que pasa (`previewStayCheckOut`: las noches
 * que se liberan y lo que se devuelve, pregunta 339 (a)). Sin pase (pendiente de pago), no se puede entrar.
 */
type Lista = 'llegadas' | 'dentro' | 'salidas';
const LISTAS: readonly { valor: Lista; texto: string }[] = [
    { valor: 'llegadas', texto: 'Llegadas de hoy' },
    { valor: 'dentro', texto: 'Dentro' },
    { valor: 'salidas', texto: 'Salidas de hoy' },
];
const PERMISOS = { completar: 'reservas.pendientes.completar', validar: 'pases.puerta.validar', asignar: 'reservas.recursos.asignar' } as const;

interface Datos {
    hoy: Reserva[];
    dentro: Reserva[];
}

const noches = (n: number | null | undefined) => (n === 1 ? '1 noche' : `${String(n ?? 0)} noches`);

export function Estancias(props: PropsPantalla & { contexto: ContextoReservas }) {
    const { ops, negocio, movil, soloLectura, contexto } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [lista, setLista] = useState<Lista>('llegadas');
    const [entrada, setEntrada] = useState<{ reserva: Reserva; alojamiento: string } | null>(null);
    const [salida, setSalida] = useState<{ reserva: Reserva; vista: VistaPreviaSalida | null; nota: string } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [locales, setLocales] = useState<Readonly<Record<string, true>>>({});
    const recargar = useCallback(() => { setIntento((i) => i + 1); }, []);
    const hoy = hoyEn(contexto.zona);
    const zonas = contexto.zonas.filter((z) => z.modality === 'stay');
    const claveZonas = zonas.map((z) => z.id).join(',');

    const sinBloque = contexto.bloques.length > 0 && !contexto.bloques.includes('estancias');
    useEffect(() => {
        if (claveZonas === '' || sinBloque) {
            return;
        }
        let vivo = true;
        // G880: todas las estancias de una vez con `kind=stay`, sin pedir zona por zona.
        Promise.all([
            todas((cursor) => ops.listReservations(negocio, { kind: 'stay', service_date: hoy, cursor, limit: 100 })),
            todas((cursor) => ops.listReservations(negocio, { kind: 'stay', status: 'arrived', cursor, limit: 100 })),
        ])
            .then(([deHoy, dentro]) => {
                if (vivo) {
                    setDatos({ hoy: deHoy, dentro });
                    setError(null);
                    setLocales({});
                }
            })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [ops, negocio, claveZonas, sinBloque, hoy, intento]);

    const todasLasReservas = [...(datos?.hoy ?? []), ...(datos?.dentro ?? [])];
    const nombres = useNombres(ops, negocio, todasLasReservas.map((r) => r.customer_id ?? '').filter((x) => x !== ''), permisos.puede('clientes.fichas.ver'));

    if (sinBloque) {
        return <p className="rv-sl-nota rv-sl-nota--centro">Las llegadas y salidas de las estancias son del bloque Estancias, que tu negocio no tiene contratado.</p>;
    }
    if (zonas.length === 0) {
        return <p className="rv-sl-nota rv-sl-nota--centro">Aún no tienes zonas de estancia. Créalas en Recursos con la modalidad «Estancia».</p>;
    }
    if (error !== null && datos === null) {
        const e = estadoDeError(error);
        return <EstadoError seccion="Estancias" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="Estancias" />;
    }

    const llegadas = datos.hoy.filter((r) => r.check_in_date === hoy && (['confirmed', 'pending_payment'].includes(r.status) || (r.status === 'arrived' && locales[r.id] === true)));
    const dentro = datos.dentro.filter((r) => r.check_out_date !== hoy);
    const salidas = datos.dentro.filter((r) => r.check_out_date === hoy);
    const filas = lista === 'llegadas' ? llegadas : lista === 'dentro' ? dentro : salidas;
    const idx = LISTAS.findIndex((l) => l.valor === lista);
    const puedeEntrar = permisos.puede(PERMISOS.validar) && !soloLectura;
    const puedeSalir = permisos.puede(PERMISOS.completar) && !soloLectura;
    const fallo = (e: unknown, defecto: string) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : defecto) }); };
    const alojamiento = (r: Reserva) => r.resources.map((a) => {
        const x = contexto.recursos.find((y) => y.id === a.resource_id);
        return `${mayuscula(contexto.zonas.find((z) => z.id === x?.current_terms.zone_id)?.unit ?? contexto.palabra)} ${x?.code ?? ''}`.trim();
    }).join(' + ');

    const llegada = (r: Reserva) => ops.listPasses(negocio, { passable_type: 'reserva', passable_id: r.id, status: 'active', limit: 1 }).then((p) => {
        const pase = p.data[0];
        if (pase === undefined) {
            throw new Error('sin pase');
        }
        return ops.validatePass(negocio, { method: 'manual', pass_id: pase.id, client_scan_id: claveIdempotencia() });
    });
    const entrar = (r: Reserva) => {
        const nombre = nombreDe(r, nombres);
        setLocales((x) => ({ ...x, [r.id]: true }));
        envio.programar({
            texto: `${nombre} ya está dentro.`,
            donde: r.id,
            enviar: () => {
                llegada(r)
                    .then((l) => { if (l.result !== 'green') { avisar({ tipo: 'error', texto: l.message ?? 'No se ha podido registrar la llegada.' }); } recargar(); })
                    .catch((e: unknown) => { fallo(e, 'No se ha podido registrar la llegada.'); recargar(); });
            },
            alDeshacer: () => { setLocales((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== r.id))); },
        });
    };
    const entrarConAlojamiento = () => {
        if (entrada === null) {
            return;
        }
        setOcupado(true);
        ops.assignReservationResources(negocio, entrada.reserva.id, { resource_ids: [entrada.alojamiento], confirm_different_resource: false })
            .then(() => llegada(entrada.reserva))
            .then(() => { avisar({ tipo: 'ok', texto: `${nombreDe(entrada.reserva, nombres)} ya está dentro.` }); setEntrada(null); recargar(); })
            .catch((e: unknown) => { fallo(e, 'No se ha podido hacer el check-in.'); })
            .finally(() => { setOcupado(false); });
    };
    const abrirSalida = (r: Reserva) => {
        setSalida({ reserva: r, vista: null, nota: '' });
        ops.previewStayCheckOut(negocio, r.id)
            .then((v) => { setSalida((x) => (x === null || x.reserva.id !== r.id ? x : { ...x, vista: v })); })
            .catch(() => undefined);
    };
    const salir = () => {
        if (salida === null) {
            return;
        }
        setOcupado(true);
        ops.checkOutStay(negocio, salida.reserva.id, salida.nota.trim() === '' ? {} : { note: salida.nota.trim() })
            .then(() => { avisar({ tipo: 'ok', texto: `Check-out de ${nombreDe(salida.reserva, nombres)} hecho.` }); setSalida(null); recargar(); })
            .catch((e: unknown) => { fallo(e, 'No se ha podido hacer el check-out.'); })
            .finally(() => { setOcupado(false); });
    };

    const vista = salida?.vista ?? null;
    const anticipada = vista?.early ?? (salida !== null && salida.reserva.check_out_date !== null && salida.reserva.check_out_date !== undefined && salida.reserva.check_out_date > hoy);
    const libres = entrada === null ? [] : contexto.recursos.filter((x) => x.current_terms.zone_id === entrada.reserva.zone_id && x.current_terms.max_capacity >= entrada.reserva.party_size);

    return (
        <>
            <p className="rv-sl-nota">{`Llegadas y salidas de hoy, ${diaLargo(hoy)}.`}</p>
            <div className="rv-sl-filtro">
                <button type="button" className="rv-sl-filtro__pastilla" aria-label={`${LISTAS[idx]?.texto ?? ''}, ${String(filas.length)}`}>
                    <span>{LISTAS[idx]?.texto}</span>
                    <b>{filas.length}</b>
                </button>
                <button type="button" className="rv-sl-redondo rv-sl-redondo--chico" aria-label="Ver la lista siguiente" onClick={() => { setLista(LISTAS[(idx + 1) % LISTAS.length]?.valor ?? 'llegadas'); }}>
                    <Icono nombre="chevron-right" />
                </button>
            </div>
            {soloLectura && <p className="rv-sl-nota">El negocio está en solo lectura: puedes ver las estancias, pero no registrar llegadas ni salidas.</p>}
            {filas.length === 0 ? (
                <p className="rv-sl-nota rv-sl-nota--centro">{lista === 'llegadas' ? 'Hoy no llega nadie.' : lista === 'dentro' ? 'Ahora no hay nadie dentro.' : 'Hoy no sale nadie.'}</p>
            ) : (
                <ul className="rv-rs-estancias" aria-label={LISTAS[idx]?.texto}>
                    {filas.map((r) => {
                        const nombre = nombreDe(r, nombres);
                        const donde = [alojamiento(r), contexto.zonas.find((z) => z.id === r.zone_id)?.name ?? '', r.check_out_date == null ? '' : `hasta el ${diaAbreviado(r.check_out_date)}`, `${String(r.party_size)} personas`].filter((x) => x !== '').join(' • ');
                        const sinPase = r.status === 'pending_payment';
                        const pendiente = envio.pendiente?.donde === r.id;
                        return (
                            <li key={r.id} className="rv-rs-estancia">
                                <span className="rv-rs-estancia__dia"><strong>{diaCorto(r.check_in_date ?? r.local_date)}</strong><small>{noches(r.nights)}</small></span>
                                <span className="rv-rs-estancia__texto">
                                    <strong>{nombre}</strong>
                                    <small>{donde}</small>
                                    {sinPase && <small><Icono nombre="info" />{' Sin pase: falta el pago. Cuando pague, podrá entrar.'}</small>}
                                </span>
                                {lista === 'llegadas' && r.resources.length === 0 && <Chip estado="pending">Sin alojamiento</Chip>}
                                {sinPase && <Chip estado="pending">Pendiente de pago</Chip>}
                                {lista !== 'llegadas' && r.arrived_at != null && <small className="rv-rs-estancia__entro">{`Entró el ${hoyEn(contexto.zona, new Date(r.arrived_at)) === hoy ? '' : `${diaCorto(hoyEn(contexto.zona, new Date(r.arrived_at)))}, `}${horaEn(r.arrived_at, contexto.zona)}`}</small>}
                                {lista === 'llegadas' && !sinPase && puedeEntrar && (
                                    pendiente && envio.pendiente !== null
                                        ? <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} />
                                        : r.status !== 'arrived' && (
                                            <button type="button" className="rv-b rv-b-fan" onClick={() => { if (r.resources.length === 0) { setEntrada({ reserva: r, alojamiento: '' }); } else { entrar(r); } }}>
                                                <Icono nombre="door-open" /><span>Hacer el check-in</span>
                                            </button>
                                        )
                                )}
                                {lista !== 'llegadas' && puedeSalir && (
                                    <button type="button" className="rv-b rv-b-fan" onClick={() => { abrirSalida(r); }}>
                                        <Icono nombre="log-out" /><span>{r.check_out_date != null && r.check_out_date > hoy ? 'Salida anticipada' : 'Hacer el check-out'}</span>
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            <Ventana
                abierta={entrada !== null}
                titulo={entrada === null ? '' : `Check-in de ${nombreDe(entrada.reserva, nombres)}`}
                texto={entrada === null ? '' : `${contexto.zonas.find((z) => z.id === entrada.reserva.zone_id)?.name ?? ''} • ${noches(entrada.reserva.nights)} • ${String(entrada.reserva.party_size)} personas. Aún no tiene alojamiento: elige uno libre.`}
                accion="Hacer el check-in"
                accionIcono="door-open"
                ocupado={ocupado}
                inactiva={entrada?.alojamiento === ''}
                movil={movil}
                onAccion={entrarConAlojamiento}
                onCerrar={() => { setEntrada(null); }}
            >
                <Desplegable
                    etiqueta="Alojamiento"
                    valor={entrada?.alojamiento ?? ''}
                    opciones={[{ valor: '', texto: 'Elige uno' }, ...libres.map((x) => ({ valor: x.id, texto: `${mayuscula(contexto.zonas.find((z) => z.id === x.current_terms.zone_id)?.unit ?? contexto.palabra)} ${x.code ?? ''}` }))]}
                    onChange={(v) => { setEntrada((x) => (x === null ? null : { ...x, alojamiento: v })); }}
                    ayuda="Si alguno está ocupado alguna de sus noches, te lo diremos al elegirlo."
                />
            </Ventana>
            <Ventana
                abierta={salida !== null}
                titulo={salida === null ? '' : `${anticipada ? 'Salida anticipada' : 'Check-out'} de ${nombreDe(salida.reserva, nombres)}`}
                texto={salida === null ? '' : anticipada && salida.reserva.check_out_date != null
                    ? `Tenía la salida el ${diaLargo(salida.reserva.check_out_date)}.${vista === null ? '' : ` Se liberan ${noches(vista.nights_released)}.`}`
                    : 'Termina su estancia.'}
                accion="Hacer el check-out"
                accionIcono="log-out"
                ocupado={ocupado}
                movil={movil}
                onAccion={salir}
                onCerrar={() => { setSalida(null); }}
            >
                {anticipada && (
                    <p className="rv-rs-presupuesto">
                        <span>
                            <Icono nombre="scale" />
                            {vista === null
                                ? ' Calculando lo que pasa con lo pagado…'
                                : ` ${vista.refund_amount == null ? 'No se le devuelve nada de esas noches' : `Se le devuelven ${dinero(vista.refund_amount)}`}. ${vista.terms_text ?? ''}`}
                        </span>
                    </p>
                )}
                <Campo etiqueta="Nota (opcional)" valor={salida?.nota ?? ''} onChange={(v) => { setSalida((x) => (x === null ? null : { ...x, nota: v })); }} max={500} ayuda="Por ejemplo: «Se va por un imprevisto»." />
            </Ventana>
        </>
    );
}
